import type { ParentDraftSchema } from '@repo/schemas/parent/parentDraft';
import type { UserCreateSchema } from '@repo/schemas/user/userCreate';
import type { UserPrivate } from '@repo/types/users';
import type { Dispatch, SetStateAction } from 'react';

import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { generatePassword } from '@repo/lib/generatePassword';
import { parentDraftSchema } from '@repo/schemas/parent/parentDraft';
import { UserIcon, UserPlusIcon, XIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useForm, useFormContext } from 'react-hook-form';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { EmptyState, EmptyStateContent, EmptyStateDescription, EmptyStateTitle } from '@/components/ui/empty-state';
import { getAvatar } from '@/utils/getAvatar';

import { ParentDraftForm } from './ParentDraftForm';
import { ParentLinkPicker } from './ParentLinkPicker';

/**
 * A new parent account drafted inline, together with the password it will be
 * created with.
 */
export interface NewParentDraft extends ParentDraftSchema {
  password: string;
}

/**
 * Props for the CreateUserParentSection component.
 * @property parents - Existing parent accounts available for linking.
 * @property selectedParentIds - Ids of existing parents selected for linking.
 * @property onSelectedParentIdsChange - State setter for the selected parent ids.
 * @property newParents - New parent accounts drafted inline.
 * @property onNewParentsChange - State setter for the drafted parents.
 */
interface CreateUserParentSectionProps {
  parents: UserPrivate[];
  selectedParentIds: number[];
  onSelectedParentIdsChange: Dispatch<SetStateAction<number[]>>;
  newParents: NewParentDraft[];
  onNewParentsChange: Dispatch<SetStateAction<NewParentDraft[]>>;
}

/**
 * Optional section of the create-user dialog for linking the new student with
 * parents: either existing parent accounts (combobox) or brand new ones
 * drafted inline and created together with the student.
 */
export function CreateUserParentSection(parentSectionProps: CreateUserParentSectionProps) {
  const { parents, selectedParentIds, onSelectedParentIdsChange, newParents, onNewParentsChange } = parentSectionProps;

  const [parentPickerOpen, setParentPickerOpen] = useState(false);
  const [draftFormOpen, setDraftFormOpen] = useState(false);

  const userForm = useFormContext<UserCreateSchema>();

  const selectedParents = useMemo(() => parents.filter((parent) => selectedParentIds.includes(parent.id)), [parents, selectedParentIds]);
  const availableParents = useMemo(() => parents.filter((parent) => !selectedParentIds.includes(parent.id)), [parents, selectedParentIds]);

  const draftForm = useForm<ParentDraftSchema>({
    defaultValues: {
      name: '',
      surname: '',
      email: '',
      phone: null,
      birthdate: null,
    },
    mode: 'onBlur',
    resolver: standardSchemaResolver(parentDraftSchema),
  });

  const onAddDraft = (draft: ParentDraftSchema) => {
    // draft.email is already lowercased and trimmed by the schema.
    if (draft.email === userForm.getValues('email').trim().toLowerCase()) {
      draftForm.setError('email', {
        message: 'Rodzic musi mieć inny adres email niż uczeń',
      });
      return;
    }
    const emailTaken = newParents.some((newParent) => newParent.email === draft.email) || parents.some((parent) => parent.email.toLowerCase() === draft.email);
    if (emailTaken) {
      draftForm.setError('email', {
        message: 'Rodzic z tym adresem email już istnieje',
      });
      return;
    }
    onNewParentsChange((drafts) => [...drafts, { ...draft, password: generatePassword(10) }]);
    draftForm.reset();
    setDraftFormOpen(false);
  };

  const onCancelDraft = () => {
    draftForm.reset();
    setDraftFormOpen(false);
  };

  const hasLinkedParents = selectedParents.length > 0 || newParents.length > 0;

  return (
    <div className={'space-y-3'}>
      <h5 className={'font-medium'}>
        Powiąż rodziców <small className={'text-text-tertiary text-sm font-normal'}>(opcjonalne)</small>
      </h5>
      <div className={'flex flex-col gap-2'}>
        {!hasLinkedParents && !draftFormOpen && (
          <EmptyState className={'border-border-secondary bg-muted/25 w-full rounded-md border pt-4!'}>
            <EmptyStateContent>
              <EmptyStateTitle className={'text-sm!'}>Brak powiązanych rodziców</EmptyStateTitle>
              <EmptyStateDescription className={'max-w-xs text-xs!'}>Powiąż istniejącego rodzica lub utwórz nowego.</EmptyStateDescription>
            </EmptyStateContent>
          </EmptyState>
        )}
        {hasLinkedParents && (
          <ul className={'flex flex-col gap-2'}>
            {selectedParents.map((parent) => (
              <li key={parent.id} className={'border-border-primary bg-card/75 flex items-center gap-3 rounded-md border p-2'}>
                <Avatar size={'sm'}>
                  <AvatarImage src={parent.avatarURL ? getAvatar(parent.avatarURL) : undefined} />
                  <AvatarFallback>
                    <UserIcon className={'size-3.5'} />
                  </AvatarFallback>
                </Avatar>
                <div className={'flex min-w-0 grow flex-col'}>
                  <p className={'truncate text-sm'}>
                    {parent.name} {parent.surname}
                  </p>
                  <p className={'text-text-tertiary truncate text-xs'}>{parent.email}</p>
                </div>
                <Button
                  size={'sm'}
                  variant={'ghost'}
                  variantColor={'destructive'}
                  onClick={() => onSelectedParentIdsChange((ids) => ids.filter((id) => id !== parent.id))}
                  aria-label={`Usuń ${parent.name} ${parent.surname}`}
                >
                  <XIcon />
                </Button>
              </li>
            ))}
            {newParents.map((draft) => (
              <li key={draft.email} className={'border-border-primary bg-card/75 flex items-center gap-3 rounded-md border p-2'}>
                <Avatar size={'sm'}>
                  <AvatarFallback>
                    <UserPlusIcon className={'size-3.5'} />
                  </AvatarFallback>
                </Avatar>
                <div className={'flex min-w-0 grow flex-col'}>
                  <p className={'truncate text-sm'}>
                    {draft.name} {draft.surname}
                  </p>
                  <p className={'text-text-tertiary truncate text-xs'}>{draft.email}</p>
                </div>
                <Badge variant={'success'}>Nowe konto</Badge>
                <Button
                  size={'sm'}
                  variant={'ghost'}
                  variantColor={'destructive'}
                  onClick={() => onNewParentsChange((drafts) => drafts.filter((d) => d.email !== draft.email))}
                  aria-label={`Usuń ${draft.name} ${draft.surname}`}
                >
                  <XIcon />
                </Button>
              </li>
            ))}
          </ul>
        )}
        {draftFormOpen && <ParentDraftForm form={draftForm} onSubmit={onAddDraft} onCancel={onCancelDraft} />}
        <ParentLinkPicker
          availableParents={availableParents}
          open={parentPickerOpen}
          onOpenChange={setParentPickerOpen}
          onSelectParent={(parent) => {
            onSelectedParentIdsChange((ids) => [...ids, parent.id]);
            setParentPickerOpen(false);
          }}
          onCreateNewParent={() => setDraftFormOpen(true)}
        />
      </div>
    </div>
  );
}
